import AbstractGenerator from '../../../misc/generator';
import AbstractObject from '../../../misc/object';

import Ground from '../objects/ground';
import Panel from '../../../objects/panel';
import Barrier from '../objects/barrier';
import Arrow from '../../../objects/arrow';
import { HTMLEditableLabel } from '../../../objects/html-label';
import { addDistanceLabel, getEndpoints, getSides, unitTransform } from '../../../../services/helper.service';
import { LabelsPadding } from '@scene/objects/ground';
import Scene from '@scene';
import { defaultUnits } from '@config';

import { generateScrewGroup } from './helper';
import * as THREE from 'three';
import { ScrewGroup } from '@scene/objects/screw';
import Cladding, { CladdingTypes } from './cladding';

export default class FloorSlab extends AbstractGenerator {

  constructor(options, dispatcher) {
    super(options);

    if (!this.options.ground) throw new Error('Ground options is required');
    if (!this.options.barrier) throw new Error('Barrier options is required');
    if (!this.options.panel) throw new Error('Panel options is required');

    this.dispatchOptionUpdate = dispatcher;

    // Update settings for floor slab object
    this.options.barrier.type = Barrier.types.STAIRS;
    this.options.ground.type = typeof this.options.ground.padding.C1y === 'number' ? Ground.types.BASE : Ground.types.FLOOR;
    this.options.barrier.tilt = 0;
    if (this.options.screw.count.n === 3) {
      this.options.panel.circleCorrection = 90;
    }

    this.panels = new AbstractObject();
  }

  init() {
    super.init();

    if (this.options.beam.shift) {
      this.barrier.mesh.position.z -= this.options.beam.shift.x;
      this.barrier.mesh.position.x -= this.options.beam.shift.y;
    }
  }

  init3DObject() {
    this.barrier = new Barrier(this.options.barrier);
    this.barrier.mesh.rotation.y = Math.PI / 2;
    this.layer3D.addObject(this.barrier);

    this.options.panel.position = Panel.positions.HORIZONTAL_Y;

    this.barrier.staysPositions.forEach((item, index) => {
      const panel = new Panel({
        ...this.options.panel//,
        //screw: this.options.screw
      });
      panel.mesh.rotation.y = Math.PI / 2;
      panel.onOptionUpdate(this.dispatchOptionUpdate);
      panel.mesh.position.set(item.z, item.y, -item.x);

      this.panels.object.addObject(panel);
      if (index === 1) {
        this.middlePanel = panel;
        this.screwGroup = generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
      } else {
        generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
      }
    });

    // Create ground object
    this.options.ground.height = this.options.barrier.dpot * this.options.barrier.stayCount;
    
    this.createGround();
    this.layer3D.addObject(this.ground);

    const GROUND_TOP = this.ground.position.y + this.ground.size.depth / 2;

    this.panels.position.set(
      this.ground.position.x,
      GROUND_TOP + this.options.panel.depth / 2,
      this.ground.position.z
    );
    
    this.layer3D.addObject(this.panels);

    const BARRIER_BOTTOM = this.barrier.size.height;

    this.barrier.position.set(
      this.panels.position.x,
      this.panels.position.y + this.options.panel.depth / 2 + BARRIER_BOTTOM + this.barrier.size.height / -2,
      this.panels.position.z
    );

    // Create screw labels
    const screwLabels = this.screwGroup.getScrewLabels();
    screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
      htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      htmlElem.position.add(this.panels.mesh.position);
      htmlElem.optimalCameraDist = 3000;
      htmlElem.zoomFactor = 0.5;
    });
    this.layer3D.addObject(screwLabels);

    if (this.options.object.dimensions.display || this.options.object.dimensions.renderForReport) {
      // display on the report regardless of the display setting
      // Create dpot label
      const dpot = this.barrier.createDpotLabel(this.dispatchOptionUpdate, this.options.object.dimensions);
      this.layer3D.addObject(dpot.arrow);
      this.layer3D.htmlLayer.add(dpot.htmlElement);

      // Create hgs label
      const hgs = this.barrier.createHgsLabel(this.dispatchOptionUpdate, this.options.object.dimensions);
      this.layer3D.addMesh(hgs.arrow);
      this.layer3D.htmlLayer.add(hgs.htmlElement);
    }
    
    if (this.options.object.dimensions.display) {
      this.addLabels();
    }

    // Create cladding between barrier posts
    if (this.options.cladding.type !== CladdingTypes.None) {
      this.claddings = new AbstractObject();
      for (let i = 0; i < this.barrier.staysPositions.length - 1; i++) {
        let cladding = new Cladding({
          type: this.options.type,
          barrier: this.options.barrier,
          beamGeometry: this.barrier.stays[0].beam && this.barrier.stays[0].beam.geometry,
          cladding: this.options.cladding,
          stayHeight: this.barrier.size.stayHeight
        });
        cladding.mesh.position.z += -this.barrier.staysPositions[i].x;
        this.claddings.object.addObject(cladding);
      }

      this.claddings.position.x = this.barrier.position.x;
      this.claddings.position.y = GROUND_TOP + this.options.panel.depth;

      this.layer3D.addObject(this.claddings);
    }
  }

  init2DObject() {
    this.layer2D.addObject(this.panels);

    // Create 2D barrier
    this.options.barrier.stayCount = 1;
    this.options.barrier.disableTop = true;

    this.barrier = new Barrier(this.options.barrier);
    this.barrier.mesh.rotation.y = Math.PI / 2;
    this.layer2D.addObject(this.barrier);

    // Create panel object
    this.options.panel.position = Panel.positions.HORIZONTAL_Y;

    const panelPosition = this.barrier.staysPositions[0];
    const panel = new Panel({
      ...this.options.panel
    });
    panel.onOptionUpdate(this.dispatchOptionUpdate);
    panel.mesh.position.set(panelPosition.z, panelPosition.y, -panelPosition.x);
    panel.mesh.rotation.y = Math.PI / 2;
    this.panels.object.addObject(panel);

    this.screwGroup = generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
    const screwLabels = this.screwGroup.getScrewLabels();
    this.layer2D.addObject(screwLabels);

    // TODO: Hot fix
    this.layer2D.htmlLayer.addLayer(panel.htmlLayer);

    // Create 2D ground object
    this.options.ground.height = this.options.panel.width + 400;
    this.createGround();
    this.layer2D.addObject(this.ground);

    let groundBottom = (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width : this.options.panel.height) / 2 
      - (this.options.panel.dy ? this.options.panel.dy.value : 0);
    let c1yPadding = typeof this.options.ground.padding.C1y === 'number' ? this.options.ground.padding.C1y : 100;
    const { htmlElement: thicknessHtmlElement } = this.ground.getDepthLabel([
      {
        x: -groundBottom + LabelsPadding - c1yPadding,
        y: -this.options.ground.height / 2 + LabelsPadding,
        z: 0
      },
      {
        x: -groundBottom + 100 - c1yPadding,
        y: -this.options.ground.height / 2 - LabelsPadding,
        z: 0
      },
      {
        x: -groundBottom + 100 - c1yPadding,
        y: -this.options.ground.height / 2 - LabelsPadding - 120,
        z: 0
      }
    ]);
    this.layer2D.htmlLayer.add(thicknessHtmlElement);


    const GROUND_TOP = this.ground.position.y + this.ground.size.depth / 2;

    this.panels.position.set(
      this.ground.position.x,
      GROUND_TOP + this.options.panel.depth / 2,
      this.ground.position.z
    );

    const { compressionLine } = panel.getCompressionLine();
    if (compressionLine) {
      compressionLine.mesh.rotation.copy(panel.rotation);
      compressionLine.mesh.position.copy(this.panels.position);
      this.layer2D.addObject(compressionLine);
      let zonesLayer = panel.createZonesLabels();
      if (zonesLayer) {
        zonesLayer.mesh.rotation.copy(panel.rotation);
        zonesLayer.mesh.position.copy(this.panels.position);
        this.layer2D.addLayer(zonesLayer);
      }
    }

    // Update barrier position
    const BARRIER_BOTTOM = this.barrier.size.height;

    this.barrier.position.set(
      this.panels.position.x,
      this.panels.position.y + this.options.panel.depth / 2 + BARRIER_BOTTOM + this.barrier.size.height / -2,
      this.panels.position.z
    );

    // Rotate 2D object to display it correct
    //this.layer2D.mesh.rotation.x = 0;
    //this.layer2D.mesh.rotation.y = Math.PI / -2;
    this.middlePanel = panel;
    this.addLabels();
  }
  
  addLabels() {
    const layer = this.is3DMode ? this.layer3D : this.layer2D;
    let screwX = (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width : this.options.panel.height) / 2  - 
      (this.options.panel.dy ? this.options.panel.dy.value : 0);
      
    let options = null;
    if (this.is3DMode) {
      options = {
        is3DMode: true,
        // rotation: new THREE.Euler(Math.PI / 2, 0, 0), //this.layer3D.mesh.rotation,
        showLabels: this.options.object.dimensions.showLabels,
        displayHorizontally: this.options.object.dimensions.displayHorizontally,
        isSafetyBarrier: true
      };
      
      // Create 3D ground thickness label
      const groundThicknessLabel = this.ground.getThicknessLabel(
        {
          x: screwX + (typeof this.ground.options.padding.C1y === 'number' ? this.ground.options.padding.C1y : 150),
          y: this.ground.position.y - this.ground.size.depth / 2,
          z: this.ground.position.z - this.ground.size.height / 2 - LabelsPadding
        },
        options
      );
      layer.addMesh(groundThicknessLabel.arrow);
      layer.htmlLayer.add(groundThicknessLabel.htmlElement);
    }

    // Create C1y label
    const c1yLabel = this.createC1yLabel(screwX, options);
    if (this.is3DMode) {
      c1yLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
      c1yLabel.htmlElement.position.set(
        c1yLabel.htmlElement.position.z, 
        c1yLabel.htmlElement.position.y + this.panels.position.y,
        -c1yLabel.htmlElement.position.x + 20
      );
      c1yLabel.htmlElement.optimalCameraDist = 3000;
      c1yLabel.htmlElement.zoomFactor = 0.5;
      if (!options.displayHorizontally) {
        // c1yLabel.htmlElement.position.z += 20;
        let c1yHalfDist = Math.max(25, typeof this.ground.options.padding.C1y === 'number' ? this.ground.options.padding.C1y : (75));
        c1yLabel.htmlElement.setDirection(
          c1yLabel.htmlElement.position.clone().setX(c1yLabel.htmlElement.position.x + c1yHalfDist),
          c1yLabel.htmlElement.position.clone().setX(c1yLabel.htmlElement.position.x - c1yHalfDist),
          false
        );
      }
    }
    this.middlePanel.mesh.add(c1yLabel.line);
    layer.htmlLayer.add(c1yLabel.htmlElement);

    // Create label for Dy
    const dyLabel = this.screwGroup.createDyLabel(null, options);
    if (this.is3DMode) {
      dyLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
      dyLabel.htmlElement.position.set(
        dyLabel.arrow.position.z - this.middlePanel.options.dy.value / 2, 
        this.panels.position.y + dyLabel.arrow.position.x + this.middlePanel.size.depth / 2,
        dyLabel.arrow.position.y + 20
      );
      dyLabel.htmlElement.optimalCameraDist = 3000;
      dyLabel.htmlElement.zoomFactor = 0.5;
      if (!options.displayHorizontally) {
        // dyLabel.htmlElement.position.z += 20;
        dyLabel.htmlElement.setDirection(
          dyLabel.htmlElement.position.clone().setX(dyLabel.htmlElement.position.x + Math.max(25, this.middlePanel.options.dy.value / 2)),
          dyLabel.htmlElement.position.clone().setX(dyLabel.htmlElement.position.x - Math.max(25, this.middlePanel.options.dy.value / 2)),
          false
        );
      }
    } else {
      dyLabel.htmlElement.position.set(
        this.panels.position.x - dyLabel.arrow.position.y,
        this.panels.position.y,
        -this.panels.position.z + dyLabel.arrow.position.z - this.middlePanel.options.dy.value / 2,
      );
    }
    layer.htmlLayer.add(dyLabel.htmlElement);

    // Create panel depth label
    let panelDepth = null;
    if (this.is3DMode) {
      panelDepth = this.middlePanel.create3DDepthLabel(options);
      this.middlePanel.mesh.add(panelDepth.object);
      panelDepth.htmlElement.position.y = this.panels.position.y; //+ this.middlePanel.options.depth / 2;
      panelDepth.htmlElement.optimalCameraDist = 3000;
      panelDepth.htmlElement.zoomFactor = 0.5;
    } else {
      panelDepth = this.middlePanel.createDepthLabel();
    }
    layer.htmlLayer.add(panelDepth.htmlElement);

    // Create width label
    const { line: widthLine, htmlElement: widthHtmlElement } = this.middlePanel.createWidthLabel(options);
    if (this.is3DMode) {
      widthHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
      widthHtmlElement.position.set(
        widthHtmlElement.position.x,
        this.panels.position.y, 
        this.panels.position.z,
      );
      widthHtmlElement.optimalCameraDist = 3000;
      widthHtmlElement.zoomFactor = 0.5;
      if (!options.displayHorizontally) {
        widthHtmlElement.setDirection(
          widthHtmlElement.position.clone().setZ(widthHtmlElement.position.z + Math.max(25, this.middlePanel.size.width / 2)),
          widthHtmlElement.position.clone().setZ(widthHtmlElement.position.z - Math.max(25, this.middlePanel.size.width / 2)),
          false
        );
      }
    }
    this.middlePanel.object.addMesh(widthLine);
    layer.htmlLayer.add(widthHtmlElement);

    // For not CIRCLE panel type
    if (this.middlePanel.options.type !== Panel.types.CIRCLE) {
      // Create height label
      const { line: heightLine, htmlElement: heightHtmlElement } = this.middlePanel.createHeightLabel(options);
      if (this.is3DMode) {
        heightHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
        heightHtmlElement.position.set(
          this.panels.position.x, 
          this.panels.position.y,
          -heightHtmlElement.position.x - 20
        );
        heightHtmlElement.optimalCameraDist = 3000;
        heightHtmlElement.zoomFactor = 0.5;
        if (!options.displayHorizontally) {
          // heightHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
          heightHtmlElement.setDirection(
            heightHtmlElement.position.clone().setX(heightHtmlElement.position.x + Math.max(25, this.middlePanel.size.height / 2)),
            heightHtmlElement.position.clone().setX(heightHtmlElement.position.x - Math.max(25, this.middlePanel.size.height / 2)),
            false
          );
        }
      }
      this.middlePanel.object.addMesh(heightLine);
      layer.htmlLayer.add(heightHtmlElement);
    } else if (this.middlePanel.options.type === Panel.types.CIRCLE) {
      const { object: circleObject, htmlElement: circleHtmlElement } = this.screwGroup.createScrewCircle(options);
      this.middlePanel.object.addMesh(circleObject);
      if (this.is3DMode) {
        circleHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
        circleHtmlElement.position.y = this.panels.position.y + this.middlePanel.options.depth / 2;
        circleHtmlElement.optimalCameraDist = 3000;
        circleHtmlElement.zoomFactor = 0.5;
        if (!options.displayHorizontally) {
          // circleHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
          circleHtmlElement.setDirection(
            circleHtmlElement.position.clone().setZ(
              circleHtmlElement.position.z + Math.max(25, this.screwGroup.options.padding.diameter / 2)),
            circleHtmlElement.position.clone().setZ(
              circleHtmlElement.position.z - Math.max(25, this.screwGroup.options.padding.diameter / 2)),
            false
          );
        }
      }
      layer.htmlLayer.add(circleHtmlElement);
    }

    if (this.middlePanel.options.type === Panel.types.SQUARE) {
      if (this.options.screw.count.n > 1) {
        // Create S1 label
        const { object: s1Object, htmlElement: s1HtmlElement } = this.middlePanel.createS1Label(
          this.screwGroup, 
          false, 
          LabelsPadding, 
          this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
          options
        );
        if (this.is3DMode) {
          s1HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
          s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
          s1HtmlElement.position.set(
            this.panels.position.x + s1HtmlElement.position.z + 20, 
            this.panels.position.y + this.middlePanel.size.depth / 2,
            this.panels.position.z
          );
          s1HtmlElement.optimalCameraDist = 3000;
          s1HtmlElement.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            s1HtmlElement.setDirection(
              s1HtmlElement.position.clone().setZ(
                s1HtmlElement.position.z + Math.max(25, this.screwGroup.options.padding.s1 / 2)),
              s1HtmlElement.position.clone().setZ(
                s1HtmlElement.position.z - Math.max(25, this.screwGroup.options.padding.s1 / 2)),
              false
            );
          }
        }
        this.middlePanel.object.addMesh(s1Object);
        layer.htmlLayer.add(s1HtmlElement);
      }

      if (this.options.screw.count.m > 1 || this.options.screw.formation === ScrewGroup.formation.TRIANGLE) {
        // Create S2 label
        const { object: s2Object, htmlElement: s2HtmlElement } = this.middlePanel.createS2Label(
          this.screwGroup, 
          true, 
          this.middlePanel.size.width / 2 + LabelsPadding, 
          this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
          options
        );
        if (s2HtmlElement && s2Object) {
          if (this.is3DMode) {
            s2HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
            s2HtmlElement.position.set(
              this.panels.position.x + s2HtmlElement.position.z, 
              this.panels.position.y + s2HtmlElement.position.y,
              -s2HtmlElement.position.x + 20
            );
            s2HtmlElement.optimalCameraDist = 3000;
            s2HtmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              // s2HtmlElement.position.z += 20;
              s2HtmlElement.setDirection(
                s2HtmlElement.position.clone().setX(
                  s2HtmlElement.position.x + Math.max(25, this.screwGroup.options.padding.s2 / 2)),
                s2HtmlElement.position.clone().setX(
                    s2HtmlElement.position.x - Math.max(25, this.screwGroup.options.padding.s2 / 2)),
                false
              );
            }
        }
        this.middlePanel.object.addMesh(s2Object);
        layer.htmlLayer.add(s2HtmlElement);
        }
      }
    }
    this.createShiftLabels(layer, options);
  }

  setOrientation() {
    super.setOrientation();

    if (this.is2DMode) {
      // Rotate 2D object to display it correct
      this.layer2D.mesh.rotation.x = 0;
      this.layer2D.mesh.rotation.y = Math.PI / -2;
    }
  }

  createGround() {
    this.screwPositions = getEndpoints(this.screwGroup.screwCoordinates);
    this.groundPadding = getSides({ ground: this.options.ground, panel: this.options.panel }, this.screwPositions);

    const panelSize = this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width : this.options.panel.height;
    // width it is height in 2D mode
    this.options.ground.width = typeof this.options.ground.padding.C1y === 'number' ? 
      (100 + panelSize + this.options.ground.padding.C1y) :
      (100 + panelSize / 2);

    if (this.options.ground.padding.C1x && this.options.ground.padding.C2x) {
      this.options.ground.width = this.groundPadding.left + this.groundPadding.right;
    }

    this.ground = new Ground(this.options.ground);
    this.ground.onOptionUpdate(this.dispatchOptionUpdate);
    this.ground.mesh.rotation.y = Math.PI;
    this.ground.mesh.rotation.x = Math.PI / 2;

    // this.ground.ground.position.x += this.groundPadding.left - this.ground.size.width / 2;
    this.ground.ground.position.x = typeof this.options.ground.padding.C1y === 'number' ? 
      -this.options.ground.padding.C1y - panelSize / 2 + (this.options.panel.dy ? this.options.panel.dy.value : 0) : 0;
  }

  createC1yLabel(startY, options) {
    const { left } = getEndpoints(this.screwGroup.screwCoordinates);

    // const panelSize = this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width : this.options.panel.height;
    const panelSize = this.options.panel.width;
    let infinityHTML = "font-family: cursive;font-size: 25px;top: -0.55rem;position: absolute";
    let horizontalPadding = (!options || !options.is3DMode || options.showLabels) ? ";right: 0rem" : ";left: 0rem";
    infinityHTML = "<span style=\"visibility: hidden;\">AB</span><span style=\"" + infinityHTML + horizontalPadding + "\">∞</span>";
    const { object: line, text: htmlElement } = addDistanceLabel({
      top: {
        from: {
          x: -left.x +1,
          y: left.y,
          z: startY 
        },
        to: {
          x: -left.x +1,
          y: panelSize / 2 + 4 * LabelsPadding,
          z: startY//bottom.z
        }
      },
      bottom: {
        from: {
          x: -left.x +1,
          y: this.ground.options.padding.C1y ? left.y : 0,
          z: startY + this.ground.options.padding.C1y
        },
        to: {
          x: -left.x +1,
          y: this.ground.options.padding.C1y ? (panelSize / 2 + 4 * LabelsPadding) : 0,
          z: startY + this.ground.options.padding.C1y
        }
      },
      size: typeof this.ground.options.padding.C1y === 'number' ? this.ground.options.padding.C1y : (150),
      arrowDirection: Arrow.types.direction.Z,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? 'C1y = ' : '') + 
        `${Number.isFinite(this.ground.options.padding.C1y) ? 
        unitTransform(this.ground.options.padding.C1y).from(defaultUnits.DISTANCE).to(Scene.units.distance) : infinityHTML}`,
      labelValue: this.ground.options.padding.C1y && 
        unitTransform(this.ground.options.padding.C1y).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.RIGHT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            padding: {
              C1y: value !== '' ? unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE) : null
            }
          }
        },
        false,
        didChange);
      }
    });

    // line.rotation.y = Math.PI / -2;
    // this.ground.object.addMesh(line);

    return {
      line,
      htmlElement
    };
  }

  createShiftLabels(layer, options) {
    const shiftLabels = this.barrier.createShiftLabels(
      this.dispatchOptionUpdate, 
      this.options.beam.shift, 
      new THREE.Vector3(this.panels.position.x, this.panels.position.z, this.panels.position.y),
      options
    );
    if (shiftLabels) {
      if (shiftLabels.marker) {
        shiftLabels.marker.position.set(
          this.panels.position.x, 
          this.panels.position.y + this.middlePanel.size.depth / 2, 
          this.panels.position.z
        );
        layer.addObject(shiftLabels.marker);
      }
      if (shiftLabels.xLabel) {
        shiftLabels.xLabel.object.rotation.set(0, 1.57, 1.57, "ZXY");
        shiftLabels.xLabel.object.position.y += this.middlePanel.size.depth / 2;
        if (options && options.is3DMode) {
          shiftLabels.xLabel.HTMLObject.verticalAlign = HTMLEditableLabel.types.align.CENTER;
          shiftLabels.xLabel.HTMLObject.position.set(
            shiftLabels.xLabel.HTMLObject.position.z + (this.options.beam.shift.y < 0 ? -20 : 20),
            shiftLabels.xLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
            -shiftLabels.xLabel.HTMLObject.position.x,
          );
          shiftLabels.xLabel.HTMLObject.optimalCameraDist = 3000;
          shiftLabels.xLabel.HTMLObject.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            shiftLabels.xLabel.HTMLObject.setDirection(
              shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                shiftLabels.xLabel.HTMLObject.position.z + Math.max(25, this.options.beam.shift.x / 2)
              ), 
              shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                shiftLabels.xLabel.HTMLObject.position.z - Math.max(25, this.options.beam.shift.x / 2)
              ),
              false
            );
          }
        }
        layer.addMesh(shiftLabels.xLabel.object);
        layer.htmlLayer.add(shiftLabels.xLabel.HTMLObject);
      }
      if (shiftLabels.yLabel) {
        shiftLabels.yLabel.object.rotation.set(0, 1.57, 1.57, "ZXY");
        shiftLabels.yLabel.object.position.y += this.middlePanel.size.depth / 2;
        if (options && options.is3DMode) {
          shiftLabels.yLabel.HTMLObject.align = HTMLEditableLabel.types.align.CENTER;
          shiftLabels.yLabel.HTMLObject.position.set(
            shiftLabels.yLabel.HTMLObject.position.z,
            shiftLabels.yLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
            -shiftLabels.yLabel.HTMLObject.position.x + (this.options.beam.shift.x < 0 ? -20 : 20),
          );
          shiftLabels.yLabel.HTMLObject.optimalCameraDist = 3000;
          shiftLabels.yLabel.HTMLObject.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            shiftLabels.yLabel.HTMLObject.setDirection(
              shiftLabels.yLabel.HTMLObject.position.clone().setX(
                shiftLabels.yLabel.HTMLObject.position.x + Math.max(25, this.options.beam.shift.y / 2)
              ), 
              shiftLabels.yLabel.HTMLObject.position.clone().setX(
                shiftLabels.yLabel.HTMLObject.position.x - Math.max(25, this.options.beam.shift.y / 2)
              ),
              false
            );
          }
        }
        layer.addMesh(shiftLabels.yLabel.object);
        layer.htmlLayer.add(shiftLabels.yLabel.HTMLObject);
      }
    }
  }

}



// WEBPACK FOOTER //
// ./src/object-view/generators/safety-barrier/objects/floor-slab.js